import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  createSupportTicket, 
  getSupportCategories, 
  uploadSupportFile 
} from '../../services/api';
import ConfirmationModal from '../../components/common/ConfirmationModal';

// Import modular components
import Loading from '../../components/common/Loading';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';

import { 
  ArrowLeft, Home, Search, Bell, User, Filter, Download,
  Calendar, X, ChevronLeft, ChevronRight, Smartphone, Monitor,
  Clock, FileText, CheckCircle, XCircle, Loader, Copy, ExternalLink,
  Receipt, Banknote, Tag, Building, Globe, Smartphone as SmartphoneIcon,
  CreditCard as CreditCardIcon, Wallet as WalletIcon, Check,
  Key, Shield as ShieldIcon, AlertTriangle, Info,
  PlusCircle, LogIn, MessageSquare, Mail, HelpCircle,
  Upload, Image as ImageIcon, File, Paperclip, Trash2, AlertCircle as AlertCircleIcon
} from 'lucide-react';

// ========== MOBILE VIEW COMPONENT ==========
const MobileView = ({ 
  title, setTitle, 
  description, setDescription, 
  categories, selectedCategory, setSelectedCategory,
  priority, setPriority,
  attachments, fileInputRef, handleFileSelect, removeAttachment,
  loading, uploading, handleSubmit, handleBack 
}) => (
  <div className="lg:hidden min-h-screen bg-gray-50 pb-20">
    {/* Mobile Header */}
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5 text-black" />
          </button>
          
          <h1 className="text-lg font-bold text-black">New Support Ticket</h1>
          
          <div className="w-10"></div> {/* Spacer for alignment */}
        </div>
      </div>
    </header>

    <main className="px-4 py-4">
      <div className="space-y-4">
        {/* Title Input */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <label className="block text-sm font-medium text-black mb-2">
            Subject
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Brief summary of your issue"
            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff660e]/20 focus:border-[#ff660e]"
            disabled={loading || uploading}
          />
        </div>

        {/* Description Input */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <label className="block text-sm font-medium text-black mb-2">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Please provide detailed information about your issue..."
            rows={6}
            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff660e]/20 focus:border-[#ff660e] resize-none"
            disabled={loading || uploading}
          />
          <p className="text-xs text-gray-500 mt-2">
            Minimum 20 characters • Current: {description.trim().length}
          </p>
        </div>

        {/* Category Selector - Mobile */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <label className="block text-sm font-medium text-black mb-3">
            Category
          </label>
          <div className="grid grid-cols-2 gap-2">
            {categories.map((category) => (
              <button
                key={category.category_id}
                onClick={() => setSelectedCategory(category)}
                disabled={loading || uploading}
                className={`p-3 border rounded-lg text-left transition ${
                  selectedCategory?.category_id === category.category_id
                    ? 'border-[#ff660e] bg-[#ff660e]/5'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  {selectedCategory?.category_id === category.category_id ? (
                    <Check className="w-4 h-4 text-[#ff660e]" />
                  ) : (
                    <div className="w-4 h-4" />
                  )}
                  <span className="text-sm font-medium">{category.name}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Priority Selector - Mobile */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <label className="block text-sm font-medium text-black mb-3">
            Priority
          </label>
          <div className="grid grid-cols-4 gap-2">
            {[
              { id: 'low', label: 'Low', color: 'bg-green-100 text-green-700' },
              { id: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-700' },
              { id: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
              { id: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700' }
            ].map((p) => (
              <button
                key={p.id}
                onClick={() => setPriority(p.id)}
                disabled={loading || uploading}
                className={`py-2.5 rounded-lg font-medium text-sm ${
                  priority === p.id 
                    ? 'border-2 border-[#ff660e]' 
                    : 'border border-gray-200'
                } ${p.color}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Attachments - Mobile */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <label className="block text-sm font-medium text-black mb-3">
            Attachments (Optional)
          </label>
          
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
            className="hidden"
            disabled={loading || uploading || attachments.length >= 5}
          />
          
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={loading || uploading || attachments.length >= 5}
            className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#ff660e] hover:bg-gray-50 flex items-center justify-center gap-2"
          >
            <Upload className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-600">
              {attachments.length >= 5 ? 'Maximum 5 files reached' : 'Add Files'}
            </span>
          </button>
          
          {/* Attachment List */}
          {attachments.length > 0 && (
            <div className="mt-3 space-y-2">
              {attachments.map((attachment, index) => (
                <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    {attachment.type === 'image' ? (
                      <ImageIcon className="w-5 h-5 text-[#ff660e]" />
                    ) : (
                      <File className="w-5 h-5 text-gray-500" />
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{attachment.fileName}</p>
                      <p className="text-xs text-gray-500">
                        {attachment.isUploading ? `Uploading... ${attachment.uploadProgress}%` :
                         attachment.uploadSuccess ? 'Uploaded' :
                         attachment.uploadError ? 'Failed' : 'Ready to upload'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeAttachment(index)}
                    disabled={attachment.isUploading || loading || uploading}
                    className="p-1.5 hover:bg-gray-100 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          <p className="text-xs text-gray-500 mt-2">
            Maximum 5 files • Images, PDFs, documents supported
          </p>
        </div>

        {/* Help Tips - Mobile */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Info className="w-5 h-5 text-blue-600" />
            <h3 className="font-medium text-black">Tips for better support</h3>
          </div>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span>Be specific about the issue you're facing</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span>Include relevant screenshots or documents</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span>Mention steps you've already tried</span>
            </li>
          </ul>
        </div>

        {/* Submit Button - Mobile */}
        <div className="sticky bottom-4 z-10">
          <button
            onClick={handleSubmit}
            disabled={loading || uploading}
            className="w-full py-4 bg-[#ff660e] text-white rounded-xl font-bold text-lg hover:bg-[#ff5500] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Creating Ticket...
              </>
            ) : uploading ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Uploading Files...
              </>
            ) : (
              'Submit Support Ticket'
            )}
          </button>
        </div>
      </div>
    </main>

    {/* Mobile Bottom Navigation */}
    <MobileBottomNav />
  </div>
);

// ========== DESKTOP VIEW COMPONENT ==========
const DesktopView = ({ 
  title, setTitle, 
  description, setDescription, 
  categories, selectedCategory, setSelectedCategory,
  priority, setPriority,
  attachments, fileInputRef, handleFileSelect, removeAttachment,
  loading, uploading, handleSubmit, handleBack 
}) => (
  <div className="hidden lg:block min-h-screen bg-gray-50">
    {/* Desktop Header */}
    <Header />
    
    <main className="max-w-5xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-black mb-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Support
          </button>
          <h1 className="text-2xl font-bold text-black">Create New Support Ticket</h1>
          <p className="text-gray-600">Describe your issue and we'll help you resolve it</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title Input */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <label className="block text-sm font-semibold text-black mb-3">
              Subject
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief summary of your issue"
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#ff660e]/20 focus:border-[#ff660e] text-lg"
              disabled={loading || uploading}
            />
          </div>

          {/* Description Input */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <label className="block text-sm font-semibold text-black mb-3">
              Detailed Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please provide detailed information about your issue. Include steps to reproduce, error messages, and any other relevant information..."
              rows={10}
              className="w-full px-4 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#ff660e]/20 focus:border-[#ff660e] resize-none"
              disabled={loading || uploading}
            />
            <div className="flex justify-between mt-3">
              <p className="text-sm text-gray-500">
                Minimum 20 characters
              </p>
              <p className={`text-sm font-medium ${
                description.trim().length < 20 ? 'text-red-600' : 'text-green-600'
              }`}>
                {description.trim().length} characters
              </p>
            </div>
          </div>

          {/* Category & Priority */}
          <div className="grid grid-cols-2 gap-6">
            {/* Category Selector */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <label className="block text-sm font-semibold text-black mb-4">
                Category
              </label>
              <div className="space-y-2">
                {categories.map((category) => (
                  <button
                    key={category.category_id}
                    onClick={() => setSelectedCategory(category)}
                    disabled={loading || uploading}
                    className={`flex items-center justify-between w-full p-3 border rounded-xl transition ${
                      selectedCategory?.category_id === category.category_id
                        ? 'border-[#ff660e] bg-[#ff660e]/5'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <span className="font-medium">{category.name}</span>
                    {selectedCategory?.category_id === category.category_id && (
                      <Check className="w-5 h-5 text-[#ff660e]" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Priority Selector */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <label className="block text-sm font-semibold text-black mb-4">
                Priority
              </label>
              <div className="space-y-3">
                {[
                  { id: 'low', label: 'Low Priority', description: 'General questions or non-urgent issues', color: 'border-green-200 bg-green-50 text-green-700' },
                  { id: 'medium', label: 'Medium Priority', description: 'Standard support requests', color: 'border-yellow-200 bg-yellow-50 text-yellow-700' },
                  { id: 'high', label: 'High Priority', description: 'Important issues affecting usage', color: 'border-orange-200 bg-orange-50 text-orange-700' },
                  { id: 'urgent', label: 'Urgent Priority', description: 'Critical issues needing immediate attention', color: 'border-red-200 bg-red-50 text-red-700' }
                ].map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setPriority(p.id)}
                    disabled={loading || uploading}
                    className={`flex flex-col items-start w-full p-4 border-2 rounded-xl transition ${
                      priority === p.id 
                        ? 'border-[#ff660e]' 
                        : 'hover:border-gray-300'
                    } ${p.color}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${p.id === 'low' ? 'bg-green-500' : p.id === 'medium' ? 'bg-yellow-500' : p.id === 'high' ? 'bg-orange-500' : 'bg-red-500'}`}></div>
                      <span className="font-semibold">{p.label}</span>
                    </div>
                    <p className="text-sm mt-2 text-left">{p.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Attachments */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <label className="block text-sm font-semibold text-black mb-4">
              Attachments (Optional)
            </label>
            
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
              className="hidden"
              disabled={loading || uploading || attachments.length >= 5}
            />
            
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-[#ff660e] hover:bg-gray-50 transition cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg font-semibold text-gray-700 mb-2">
                Click to add files or drag and drop
              </p>
              <p className="text-sm text-gray-500">
                Maximum 5 files • Images, PDFs, documents supported
              </p>
            </div>
            
            {/* Attachment List */}
            {attachments.length > 0 && (
              <div className="mt-6 space-y-3">
                <h4 className="font-semibold text-black mb-3">Selected Files ({attachments.length}/5)</h4>
                {attachments.map((attachment, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-lg ${attachment.type === 'image' ? 'bg-[#ff660e]/10' : 'bg-gray-100'}`}>
                        {attachment.type === 'image' ? (
                          <ImageIcon className="w-6 h-6 text-[#ff660e]" />
                        ) : (
                          <File className="w-6 h-6 text-gray-500" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{attachment.fileName}</p>
                        <div className="flex items-center gap-4 mt-1">
                          <p className="text-sm text-gray-500">
                            {Math.round(attachment.size / 1024)} KB
                          </p>
                          <p className={`text-sm font-medium ${
                            attachment.isUploading ? 'text-yellow-600' :
                            attachment.uploadSuccess ? 'text-green-600' :
                            attachment.uploadError ? 'text-red-600' : 'text-gray-500'
                          }`}>
                            {attachment.isUploading ? `Uploading... ${attachment.uploadProgress}%` :
                             attachment.uploadSuccess ? 'Uploaded ✓' :
                             attachment.uploadError ? 'Upload failed' : 'Ready to upload'}
                          </p>
                        </div>
                        {attachment.uploadError && (
                          <p className="text-xs text-red-600 mt-1">{attachment.uploadError}</p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeAttachment(index);
                      }}
                      disabled={attachment.isUploading || loading || uploading}
                      className="p-2 hover:bg-red-50 hover:text-red-600 rounded-lg transition"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Tips & Submit */}
        <div className="space-y-6">
          {/* Tips Section */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Info className="w-6 h-6 text-blue-600" />
              <h3 className="font-semibold text-black">Tips for Faster Resolution</h3>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-semibold text-sm">1</span>
                </div>
                <div>
                  <p className="font-medium text-black">Be Specific</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Include exact error messages, URLs, and timestamps
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-semibold text-sm">2</span>
                </div>
                <div>
                  <p className="font-medium text-black">Add Screenshots</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Visual evidence helps us understand your issue better
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-semibold text-sm">3</span>
                </div>
                <div>
                  <p className="font-medium text-black">Steps to Reproduce</p>
                  <p className="text-sm text-gray-600 mt-1">
                    List the exact steps that lead to the issue
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-semibold text-sm">4</span>
                </div>
                <div>
                  <p className="font-medium text-black">What You've Tried</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Mention any troubleshooting steps you've already taken
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="font-semibold text-black mb-4">Ready to Submit</h3>
            
            <div className="space-y-3 mb-6">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Subject</span>
                <span className={`font-medium ${title.trim() ? 'text-green-600' : 'text-red-600'}`}>
                  {title.trim() ? '✓' : 'Required'}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Description</span>
                <span className={`font-medium ${description.trim().length >= 20 ? 'text-green-600' : 'text-red-600'}`}>
                  {description.trim().length >= 20 ? '✓' : `${description.trim().length}/20`}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Category</span>
                <span className={`font-medium ${selectedCategory ? 'text-green-600' : 'text-red-600'}`}>
                  {selectedCategory ? '✓' : 'Required'}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Attachments</span>
                <span className="font-medium text-gray-700">
                  {attachments.length} files
                </span>
              </div>
            </div>
            
            <button
              onClick={handleSubmit}
              disabled={loading || uploading || !title.trim() || description.trim().length < 20 || !selectedCategory}
              className="w-full py-4 bg-[#ff660e] text-white rounded-xl font-bold hover:bg-[#ff5500] disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-3"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Creating Ticket...
                </>
              ) : uploading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Uploading Files...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Submit Support Ticket
                </>
              )}
            </button>
            
            <p className="text-xs text-gray-500 text-center mt-3">
              We'll respond to your ticket within 24 hours
            </p>
          </div>
        </div>
      </div>
    </main>
    
    {/* Desktop Footer */}
    <Footer />
  </div>
);

// ========== MAIN COMPONENT ==========
const NewSupportTicketScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Modal states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdTicketId, setCreatedTicketId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchCategories();
  }, [user, navigate]);

  const fetchCategories = async () => {
    try {
      const response = await getSupportCategories();
      if (response.success) {
        setCategories(response.data || []);
      }
    } catch (error) {
      // console.error('Error fetching categories:', error);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const newFiles = files.slice(0, 5 - attachments.length).map((file) => {
      const extension = file.name.split('.').pop()?.toLowerCase();
      const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension);
      
      return {
        file: file,
        fileName: file.name,
        type: isImage ? 'image' : 'file',
        size: file.size,
        mimeType: file.type,
        isUploading: false,
        uploadProgress: 0,
        uploadError: null,
        uploadSuccess: false,
        previewUrl: isImage ? URL.createObjectURL(file) : null,
      };
    });
    
    setAttachments([...attachments, ...newFiles]);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index) => {
    const attachment = attachments[index];
    // Revoke object URL if it's an image preview
    if (attachment.previewUrl) {
      URL.revokeObjectURL(attachment.previewUrl);
    }
    
    const newAttachments = [...attachments];
    newAttachments.splice(index, 1);
    setAttachments(newAttachments);
  };

  const uploadAttachment = async (attachment, ticketId, index) => {
    try {
      // Update attachment state
      const updatedAttachments = [...attachments];
      updatedAttachments[index] = {
        ...updatedAttachments[index],
        isUploading: true,
        uploadProgress: 0,
        uploadError: null,
      };
      setAttachments(updatedAttachments);

      // Create FormData
      const formData = new FormData();
      formData.append('file', attachment.file);
      formData.append('ticket_id', ticketId.toString());
      formData.append('user_id', user.user_id.toString());

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setAttachments(prev => {
          const newAttachments = [...prev];
          if (newAttachments[index]) {
            newAttachments[index] = {
              ...newAttachments[index],
              uploadProgress: Math.min(newAttachments[index].uploadProgress + 20, 90)
            };
          }
          return newAttachments;
        });
      }, 300);

      // Upload file
      const uploadResult = await uploadSupportFile(formData);
      
      clearInterval(progressInterval);
      
      if (uploadResult.success) {
        updatedAttachments[index] = {
          ...updatedAttachments[index],
          isUploading: false,
          uploadProgress: 100,
          uploadSuccess: true,
          attachmentId: uploadResult.attachment_id,
          fileUrl: uploadResult.file_url,
        };
        setAttachments(updatedAttachments);
        
        return { success: true, data: uploadResult };
      } else {
        throw new Error(uploadResult.error || 'Upload failed');
      }
    } catch (error) {
      // console.error(`Upload failed:`, error);
      
      const updatedAttachments = [...attachments];
      updatedAttachments[index] = {
        ...updatedAttachments[index],
        isUploading: false,
        uploadError: error.message,
        uploadProgress: 0,
      };
      setAttachments(updatedAttachments);
      
      return { success: false, error: error.message };
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!title.trim()) {
      alert('Please enter a title for your support ticket.');
      return;
    }

    if (!description.trim() || description.trim().length < 20) {
      alert('Please provide a detailed description (at least 20 characters).');
      return;
    }

    if (!selectedCategory) {
      alert('Please select a category for your support ticket.');
      return;
    }

    try {
      setLoading(true);

      // 1. Create ticket
      const ticketData = {
        user_id: user.user_id,
        category_id: selectedCategory.category_id,
        title: title.trim(),
        description: description.trim(),
        priority: priority,
      };

      const response = await createSupportTicket(ticketData);

      if (response.success) {
        const ticketId = response.ticket_id;
        setCreatedTicketId(ticketId);
        
        // 2. Upload attachments
        let successfulUploads = 0;
        let failedUploads = 0;
        
        if (attachments.length > 0) {
          setUploading(true);
          
          for (let i = 0; i < attachments.length; i++) {
            const result = await uploadAttachment(attachments[i], ticketId, i);
            if (result.success) {
              successfulUploads++;
            } else {
              failedUploads++;
            }
            // Small delay between uploads to prevent overwhelming the server
            if (i < attachments.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 300));
            }
          }
          
          setUploading(false);
        }

        // 3. Set success message
        let message = `Your support ticket #${ticketId} has been created.`;
        if (attachments.length > 0) {
          message += ` ${successfulUploads} file(s) uploaded successfully.`;
          if (failedUploads > 0) {
            message += ` ${failedUploads} file(s) failed to upload.`;
          }
        }
        setSuccessMessage(message);
        
        // 4. Show success modal
        setShowSuccessModal(true);
        
      } else {
        throw new Error(response.error || 'Failed to create ticket');
      }
    } catch (error) {
      // console.error('Error creating support ticket:', error);
      alert(error.message || 'Failed to create support ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessModalConfirm = (action) => {
    setShowSuccessModal(false);
    if (action === 'view') {
      navigate(`/support/tickets/${createdTicketId}`);
    } else {
      navigate('/support');
    }
  };

  const handleBack = () => {
    if (title.trim() || description.trim() || attachments.length > 0) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  if (loading && !uploading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading text="Creating support ticket..." />
      </div>
    );
  }

  return (
    <>
      {/* Render Mobile or Desktop View */}
      <MobileView 
        title={title}
        setTitle={setTitle}
        description={description}
        setDescription={setDescription}
        categories={categories}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        priority={priority}
        setPriority={setPriority}
        attachments={attachments}
        fileInputRef={fileInputRef}
        handleFileSelect={handleFileSelect}
        removeAttachment={removeAttachment}
        loading={loading}
        uploading={uploading}
        handleSubmit={handleSubmit}
        handleBack={handleBack}
      />
      
      <DesktopView 
        title={title}
        setTitle={setTitle}
        description={description}
        setDescription={setDescription}
        categories={categories}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        priority={priority}
        setPriority={setPriority}
        attachments={attachments}
        fileInputRef={fileInputRef}
        handleFileSelect={handleFileSelect}
        removeAttachment={removeAttachment}
        loading={loading}
        uploading={uploading}
        handleSubmit={handleSubmit}
        handleBack={handleBack}
      />
      
      {/* Success Modal */}
      <ConfirmationModal
        isOpen={showSuccessModal}
        onClose={() => handleSuccessModalConfirm('back')}
        onConfirm={() => handleSuccessModalConfirm('view')}
        title="Support Ticket Created!"
        message={successMessage}
        confirmText="View Ticket"
        cancelText="Back to Support"
        showCancel={true}
      />
    </>
  );
};

export default NewSupportTicketScreen;